import { Spinner } from '@consigli/facade';
import {
  useGetBlobsQuery,
  useGetDocumentPackageResultsQuery,
  useGetPackageQuery,
  useGetProcessorJobsQuery,
  useGetProcessorJobStageTasksQuery,
  useOverwriteBlobContentMutation,
  useProjectId,
  useStartPipelineMutation,
  useUpdatePackageMutation,
} from '@consigli/hooks';
import { JobType, PipelineName } from '@consigli/types';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';

import { useCreateInsightPackage } from '@/features/package-creation/hooks/use-create-insight-package';
import { useUploadProgress } from '@/features/package-creation/hooks/use-upload-progress';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { Route } from '@/routes';

import { EmptyState } from './components/empty-state';
import { GeneratedReportsList } from './components/generated-reports-list';
import { PrecheckContent } from './components/precheck-content';
import { ProcessingMessage } from './components/processing-message';
import { TemplateEditor } from './components/template-editor';
import { PrecheckResult } from './types';
import { getProcessingMessageKeys, PipelineStage } from './utils/pipeline-helpers';

export const Results: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();

  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);
  const { uploadFiles } = useCreateInsightPackage({
    setCurrentCounter,
    setTargetCounter,
  });
  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const [templateContent, setTemplateContent] = useState<string>('');
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [isStartingPipeline, setIsStartingPipeline] = useState(false);

  const {
    data: resultsData,
    isLoading: isLoadingResults,
    error: resultsError,
  } = useGetDocumentPackageResultsQuery(
    {
      projectId,
      documentPackageId: packageId!,
      page: 'all',
    },
    {
      skip: !packageId,
    },
  );

  const { data: packageData } = useGetPackageQuery(
    {
      projectId,
      packageId: packageId!,
    },
    {
      skip: !packageId,
    },
  );

  useEffect(() => {
    if (packageId) {
      const currentPath = window.location.pathname;
      const expectedPath = `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${Route.REPORT_GENERATION}/${Route.PACKAGES}/${packageId}/${Route.RESULTS}`;

      if (!currentPath.endsWith(`/${Route.RESULTS}`)) {
        navigate(expectedPath, { replace: true });
      }
    }
  }, [packageId, projectId, navigate]);

  const { data: blobs } = useGetBlobsQuery(
    {
      projectId,
      packageId: packageId!,
      page: 'all',
    },
    {
      skip: !packageId,
    },
  );

  const [startPipeline] = useStartPipelineMutation();
  const [overwriteBlobContent] = useOverwriteBlobContentMutation();
  const [updatePackage] = useUpdatePackageMutation();

  const currentStage: PipelineStage =
    (packageData?.data?.currentStage as PipelineStage) || PipelineStage.IDLE;

  const { data: jobsResponse } = useGetProcessorJobsQuery(
    {
      projectId,
      packageId: packageId!,
    },
    {
      skip: !packageId,
    },
  );

  const precheckJob = useMemo(
    () => jobsResponse?.find((job) => job.pipelineName === PipelineName.REPORT_GENERATION_PRECHECK),
    [jobsResponse],
  );

  const { data: precheckStageTasksResponse } = useGetProcessorJobStageTasksQuery(
    {
      projectId,
      jobId: precheckJob?.id ?? '',
      stageId: 'precheck',
      page: 'all',
    },
    {
      skip: !precheckJob?.id,
    },
  );

  const precheckResult = useMemo(() => {
    if (!precheckStageTasksResponse?.results || precheckStageTasksResponse.results.length === 0) {
      return null;
    }
    const task = precheckStageTasksResponse.results[0] as {
      resultData?: { result?: PrecheckResult } | null;
    };
    if (task.resultData && typeof task.resultData === 'object' && 'result' in task.resultData) {
      return task.resultData.result;
    }
    return null;
  }, [precheckStageTasksResponse]);

  const generatedTemplateBlob = useMemo(() => {
    if (!blobs?.results) return null;
    return blobs.results.find((blob) => blob.name === 'generated_report_template.md');
  }, [blobs]);

  useEffect(() => {
    const fetchTemplateContent = async () => {
      if (!generatedTemplateBlob || currentStage !== PipelineStage.TEMPLATE_COMPLETE) return;

      setIsLoadingTemplate(true);
      try {
        const blobWithUrl = generatedTemplateBlob as { fileUrl?: string; downloadUrl?: string };
        const fileUrl = blobWithUrl.fileUrl || blobWithUrl.downloadUrl;

        if (!fileUrl) {
          throw new Error('File URL not found in blob data');
        }

        const fileResponse = await fetch(fileUrl);
        if (fileResponse.ok) {
          const content = await fileResponse.text();
          setTemplateContent(content);
          setEditedContent(content);
        } else {
          throw new Error(
            `Failed to fetch file: ${fileResponse.status} ${fileResponse.statusText}`,
          );
        }
      } catch (error) {
        console.error('Failed to fetch generated template:', error);
        toast.error(t('package-creation.reports.failed-to-load-template'));
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    fetchTemplateContent();
  }, [generatedTemplateBlob, currentStage, t]);

  const handleSaveTemplate = useCallback(async () => {
    if (!generatedTemplateBlob) {
      toast.error(t('package-creation.reports.no-template-found'));
      return;
    }

    try {
      await overwriteBlobContent({
        projectId,
        packageId: packageId!,
        blobId: generatedTemplateBlob.id,
        content: editedContent,
      }).unwrap();

      setTemplateContent(editedContent);
      setHasUnsavedChanges(false);
      setIsEditMode(false);
      toast.success(t('package-creation.reports.changes-saved'));
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error(t('package-creation.reports.save-failed'));
    }
  }, [editedContent, generatedTemplateBlob, projectId, packageId, overwriteBlobContent, t]);

  const handleCancelEdit = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmCancel = window.confirm(t('package-creation.reports.discard-changes'));
      if (!confirmCancel) return;
    }
    setEditedContent(templateContent);
    setHasUnsavedChanges(false);
    setIsEditMode(false);
  }, [templateContent, hasUnsavedChanges, t]);

  const handleEditChange = useCallback(
    (value: string) => {
      setEditedContent(value);
      setHasUnsavedChanges(value !== templateContent);
    },
    [templateContent],
  );

  const handleRunPrecheck = useCallback(async () => {
    if (hasUnsavedChanges) {
      toast.warning(t('package-creation.reports.save-before-precheck'));
      return;
    }

    try {
      setIsStartingPipeline(true);

      await startPipeline({
        projectId,
        packageId: packageId!,
        pipelineName: PipelineName.REPORT_GENERATION_PRECHECK,
        notify: true,
        jobType: JobType.DOCUMENT_PACKAGE,
      }).unwrap();

      await updatePackage({
        projectId,
        packageId: packageId!,
        data: {
          data: {
            ...packageData?.data,
            currentStage: PipelineStage.PRECHECK_IN_PROGRESS,
          },
        },
        isHidden: false,
      }).unwrap();

      toast.success(t('package-creation.reports.precheck-started'));
    } catch (error) {
      console.error('❌ Failed to start precheck:', error);
      toast.error(t('package-creation.reports.precheck-failed'));
    } finally {
      setIsStartingPipeline(false);
    }
  }, [hasUnsavedChanges, projectId, packageId, packageData, updatePackage, startPipeline, t]);

  const handleGenerateReport = useCallback(async () => {
    try {
      setIsStartingPipeline(true);

      const hasFilesToUpload = localFiles.length > 0;

      if (hasFilesToUpload) {
        await uploadFiles(localFiles, packageId!);
        toast.success(t('package-creation.reports.files-uploaded'));
        setLocalFiles([]);
      }

      const pipelineName = hasFilesToUpload
        ? PipelineName.REPORT_GENERATION
        : PipelineName.REPORT_GENERATION_ONLY;

      await startPipeline({
        projectId,
        packageId: packageId!,
        pipelineName,
        notify: true,
        jobType: JobType.DOCUMENT_PACKAGE,
      }).unwrap();

      await updatePackage({
        projectId,
        packageId: packageId!,
        data: {
          data: {
            ...packageData?.data,
            currentStage: PipelineStage.REPORT_IN_PROGRESS,
          },
        },
        isHidden: false,
      }).unwrap();

      toast.success(t('package-creation.reports.report-generation-started'));
    } catch (error) {
      console.error('❌ Failed to start report generation:', error);
      toast.error(t('package-creation.reports.report-generation-failed'));
    } finally {
      setIsStartingPipeline(false);
    }
  }, [projectId, packageId, packageData, localFiles, uploadFiles, updatePackage, startPipeline, t]);

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={false}>
        {isLoadingResults ? (
          <div className="w-full h-full flex items-center justify-center">
            <Spinner />
          </div>
        ) : resultsError ? (
          <div className="px-4 sm:px-6 md:px-8 w-full">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Failed to load results. Please try again.</p>
            </div>
          </div>
        ) : (
          <div className="px-4 sm:px-6 md:px-8 w-full py-8">
            {(() => {
              switch (currentStage) {
                case PipelineStage.TEMPLATE_IN_PROGRESS:
                case PipelineStage.PRECHECK_IN_PROGRESS:
                case PipelineStage.REPORT_IN_PROGRESS: {
                  const processingMsgKeys = getProcessingMessageKeys(currentStage);
                  return processingMsgKeys ? (
                    <ProcessingMessage
                      title={t(processingMsgKeys.titleKey)}
                      message={t(processingMsgKeys.messageKey)}
                      showEmailNotification={processingMsgKeys.showEmailNotification}
                    />
                  ) : null;
                }

                case PipelineStage.TEMPLATE_COMPLETE:
                  return (
                    <TemplateEditor
                      templateContent={templateContent}
                      editedContent={editedContent}
                      isEditMode={isEditMode}
                      hasUnsavedChanges={hasUnsavedChanges}
                      isLoadingTemplate={isLoadingTemplate}
                      onEditChange={handleEditChange}
                      onSave={handleSaveTemplate}
                      onCancel={handleCancelEdit}
                      onStartEdit={() => setIsEditMode(true)}
                      onRunPrecheck={handleRunPrecheck}
                      isStartingPipeline={isStartingPipeline}
                    />
                  );

                case PipelineStage.PRECHECK_COMPLETE:
                  return precheckResult ? (
                    <PrecheckContent
                      precheckResult={precheckResult}
                      localFiles={localFiles}
                      setLocalFiles={setLocalFiles}
                      isUploading={isUploading}
                      progress={progress}
                      onGenerateReport={handleGenerateReport}
                      isStartingPipeline={isStartingPipeline}
                    />
                  ) : (
                    <EmptyState
                      title={t(
                        'package-creation.reports.loading-precheck-results',
                        'Loading Precheck Results',
                      )}
                      message={t(
                        'package-creation.reports.loading-precheck-results-message',
                        'Please wait while we load the validation results...',
                      )}
                    />
                  );

                case PipelineStage.REPORT_COMPLETE:
                  return resultsData?.results && resultsData.results.length > 0 ? (
                    <GeneratedReportsList results={resultsData.results} />
                  ) : (
                    <EmptyState
                      title={t(
                        'package-creation.reports.no-reports-available',
                        'No Reports Available',
                      )}
                      message={t(
                        'package-creation.reports.no-reports-available-message',
                        'Report generation completed but no reports were found.',
                      )}
                    />
                  );

                case PipelineStage.REPORT_FAILED:
                  return (
                    <EmptyState
                      icon="error"
                      title={t(
                        'package-creation.reports.report-generation-failed-title',
                        'Report Generation Failed',
                      )}
                      message={t(
                        'package-creation.reports.report-generation-failed-message',
                        'An error occurred while generating your report. Please try again or contact support.',
                      )}
                    />
                  );

                case PipelineStage.IDLE:
                default:
                  return (
                    <EmptyState
                      title={t(
                        'package-creation.reports.no-reports-generated-yet',
                        'No Reports Generated Yet',
                      )}
                      message={t(
                        'package-creation.reports.no-reports-generated-yet-message',
                        'Generated reports will appear here once the report generation process completes.',
                      )}
                    />
                  );
              }
            })()}
          </div>
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
